import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth, storage } from '../../firebase';
import { collection, query, orderBy, limit, getDocs, doc, setDoc, deleteDoc, getDoc, serverTimestamp, where, writeBatch, increment } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { FaArrowLeft, FaSearch, FaTimes } from 'react-icons/fa';

const ClubSearchPage = () => {
  const [clubs, setAllClubs] = useState([]);
  const [followedIds, setFollowedIds] = useState(new Set());
  const [currentTab, setCurrentTab] = useState('SEARCH'); // 'SEARCH' or 'FOLLOWED'
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) {
        navigate('/');
        return;
      }

      // 1. Get followed IDs
      const followedSnap = await getDocs(collection(db, 'users', user.uid, 'followedClubs'));
      const fIds = new Set();
      followedSnap.forEach(doc => fIds.add(doc.id));
      setFollowedIds(fIds);

      // 2. Get clubs
      const q = query(collection(db, 'Clubs'), orderBy('createdAt', 'desc'), limit(100));
      const clubsSnap = await getDocs(q);
      const clubsList = [];

      for (const clubDoc of clubsSnap.docs) {
        const data = clubDoc.data();
        const club = {
          id: clubDoc.id,
          name: data.clubName || 'Névtelen egyesület',
          location: data.location || 'Ismeretlen helyszín',
          imageUrl: data.profileImageUrl || data.logoUrl || '',
          ...data
        };

        // Handle Storage images if URL is missing
        if (!club.imageUrl) {
          try {
            const logoRef = ref(storage, `club_logos/${club.id}`);
            club.imageUrl = await getDownloadURL(logoRef);
          } catch (e) {
            // Default stays
          }
        }

        clubsList.push(club);
      }

      setAllClubs(clubsList);
      setLoading(false);
    };

    fetchData();
  }, [navigate]);

  const toggleFollow = async (e, club) => {
    e.stopPropagation();
    const user = auth.currentUser;
    if (!user) return;

    const isFollowing = followedIds.has(club.id);
    const clubRef = doc(db, 'users', user.uid, 'followedClubs', club.id);
    const followerRef = doc(db, 'Clubs', club.id, 'followers', user.uid);
    const clubDocRef = doc(db, 'Clubs', club.id);

    try {
      const batch = writeBatch(db);
      if (isFollowing) {
        batch.delete(clubRef);
        batch.delete(followerRef);
        batch.update(clubDocRef, { followersCount: increment(-1) });
        await batch.commit();
        setFollowedIds(prev => {
          const next = new Set(prev);
          next.delete(club.id);
          return next;
        });
      } else {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        const userData = userDocSnap.exists() ? userDocSnap.data() : {};
        const fullName = userData.fullName || userData.displayName || `${userData.lastName || ''} ${userData.firstName || ''}`.trim() || userData.name || user.email || "Íjász";
        const clubName = userData.clubName || userData.club || "Egyéni induló";
        const profileImg = userData.profileImageUrl || userData.photoUrl || "";

        batch.set(clubRef, { followedAt: serverTimestamp() });
        batch.set(followerRef, {
          uid: user.uid,
          fullName: fullName,
          club: clubName,
          profileImageUrl: profileImg,
          followedAt: serverTimestamp()
        });
        batch.update(clubDocRef, { followersCount: increment(1) });
        await batch.commit();

        setFollowedIds(prev => {
          const next = new Set(prev);
          next.add(club.id);
          return next;
        });
      }
    } catch (error) {
      console.error("Error toggling follow:", error);
    }
  };

  const displayClubs = useMemo(() => {
    let filtered = clubs.filter(club => {
      const matchesTab = currentTab === 'SEARCH' || followedIds.has(club.id);
      const matchesSearch = (club.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (club.location || '').toLowerCase().includes(searchTerm.toLowerCase());
      return matchesTab && matchesSearch;
    });

    if (currentTab === 'SEARCH' && !searchTerm) {
      return filtered.slice(0, 5);
    }
    return filtered;
  }, [clubs, followedIds, currentTab, searchTerm]);

  const sectionHeaderTitle = useMemo(() => {
    if (currentTab === 'FOLLOWED') {
      return 'Követett egyesületek:';
    }
    if (searchTerm) {
      return 'Keresési találatok:';
    }
    return 'A legújabb regisztrált egyesületek:';
  }, [currentTab, searchTerm]);

  return (
    <div style={pageContainerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <button onClick={() => navigate(-1)} style={backButtonStyle}>
          <FaArrowLeft />
        </button>
        <h2 style={titleStyle}>Egyesületek</h2>
      </div>

      {/* Search Bar */}
      <div style={searchContainerStyle}>
        <div style={searchBarWrapperStyle}>
          <FaSearch style={searchIconStyle} />
          <input
            type="text"
            placeholder="Egyesület keresése..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} style={clearButtonStyle}>
              <FaTimes />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={tabsContainerStyle}>
        <div
          onClick={() => setCurrentTab('SEARCH')}
          style={currentTab === 'SEARCH' ? activeTabStyle : inactiveTabStyle}
        >
          KERESÉS
          <div style={currentTab === 'SEARCH' ? activeIndicatorStyle : inactiveIndicatorStyle} />
        </div>
        <div
          onClick={() => setCurrentTab('FOLLOWED')}
          style={currentTab === 'FOLLOWED' ? activeTabStyle : inactiveTabStyle}
        >
          KÖVETETT
          <div style={currentTab === 'FOLLOWED' ? activeIndicatorStyle : inactiveIndicatorStyle} />
        </div>
      </div>

      {/* List */}
      <div style={listContainerStyle}>
        {!loading && (
          <h4 style={{ margin: '0 0 12px 0', color: '#b30000', fontSize: '0.95rem', fontWeight: 'bold' }}>
            {sectionHeaderTitle}
          </h4>
        )}
        {loading ? (
          <div style={centerTextStyle}>Betöltés... 🏹</div>
        ) : displayClubs.length > 0 ? (
          displayClubs.map(club => (
            <div key={club.id} onClick={() => navigate(`/egyesulet/${club.id}`)} style={cardStyle}>
              <img
                src={club.imageUrl || '/profil_def_v1.png'}
                alt="Logo"
                style={logoStyle}
              />
              <div style={cardInfoStyle}>
                <div style={clubNameStyle}>{club.name}</div>
                <div style={clubLocationStyle}>{club.location}</div>
              </div>
              <button
                onClick={(e) => toggleFollow(e, club)}
                style={followedIds.has(club.id) ? followedButtonStyle : followButtonStyle}
              >
                {followedIds.has(club.id) ? 'Követed' : 'Követés'}
              </button>
            </div>
          ))
        ) : (
          <div style={centerTextStyle}>Nem található egyesület.</div>
        )}
      </div>
    </div>
  );
};

// --- STYLES ---
const pageContainerStyle = {
  backgroundColor: '#fdfdfd',
  minHeight: '100vh',
  paddingTop: '16px',
  paddingBottom: '80px'
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '0 16px',
  height: '56px',
  backgroundColor: '#D32F2F',
  color: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  position: 'sticky',
  top: 0,
  zIndex: 100
};

const backButtonStyle = {
  background: 'none',
  border: 'none',
  color: 'white',
  fontSize: '20px',
  cursor: 'pointer',
  padding: '8px',
  display: 'flex',
  alignItems: 'center'
};

const titleStyle = {
  margin: '0 0 0 16px',
  fontSize: '20px',
  fontWeight: 'bold'
};

const searchContainerStyle = {
  padding: '16px'
};

const searchBarWrapperStyle = {
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '0 12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  border: '1px solid #eee'
};

const searchIconStyle = {
  color: '#757575'
};

const searchInputStyle = {
  flex: 1,
  height: '48px',
  border: 'none',
  padding: '0 12px',
  fontSize: '16px',
  outline: 'none',
  backgroundColor: 'transparent',
  color: '#212121'
};

const clearButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#757575',
  cursor: 'pointer',
  padding: '8px'
};

const tabsContainerStyle = {
  display: 'flex',
  borderBottom: '1px solid #eee',
  marginBottom: '8px'
};

const baseTabStyle = {
  flex: 1,
  textAlign: 'center',
  padding: '12px 0',
  fontSize: '14px',
  fontWeight: 'bold',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  transition: '0.2s'
};

const activeTabStyle = {
  ...baseTabStyle,
  color: '#D32F2F'
};

const inactiveTabStyle = {
  ...baseTabStyle,
  color: '#757575'
};

const activeIndicatorStyle = {
  width: '60px',
  height: '3px',
  backgroundColor: '#D32F2F',
  marginTop: '4px'
};

const inactiveIndicatorStyle = {
  ...activeIndicatorStyle,
  visibility: 'hidden'
};

const listContainerStyle = {
  padding: '0 12px'
};

const cardStyle = {
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '10px',
  marginBottom: '10px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  cursor: 'pointer'
};

const logoStyle = {
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  objectFit: 'cover'
};

const cardInfoStyle = {
  flex: 1,
  marginLeft: '12px',
  marginRight: '8px',
  overflow: 'hidden'
};

const clubNameStyle = {
  fontSize: '15px',
  fontWeight: 'bold',
  color: '#212121',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
};

const clubLocationStyle = {
  fontSize: '13px',
  color: '#757575',
  marginTop: '2px'
};

const followButtonStyle = {
  backgroundColor: '#D32F2F',
  color: 'white',
  border: 'none',
  borderRadius: '16px',
  padding: '6px 16px',
  fontSize: '11px',
  fontWeight: 'bold',
  cursor: 'pointer'
};

const followedButtonStyle = {
  ...followButtonStyle,
  backgroundColor: '#757575'
};

const centerTextStyle = {
  textAlign: 'center',
  padding: '40px',
  color: '#757575'
};

export default ClubSearchPage;
